import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import { GoogleLogin } from "@react-oauth/google";

import SteamSignInButton from "../auth/SteamSignInButton";
import { PUBLIC_NAV_LINKS } from "./siteData";
import { useTemplateTrack } from "../templates/TemplateTrackingProvider";

export default function PublicHeader({
  isAuthenticated,
  googleResponse,
  onFailure,
}) {
  const location = useLocation();
  const track = useTemplateTrack();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [loginAnchorEl, setLoginAnchorEl] = React.useState(null);

  const ctaHref = location.pathname === "/" ? "#get-started" : "/#get-started";
  const isLoginPopoverOpen = Boolean(loginAnchorEl);

  React.useEffect(() => {
    setLoginAnchorEl(null);
  }, [location.pathname]);

  const navButtonSx = {
    color: "rgba(255,255,255,0.78)",
    textTransform: "none",
    fontWeight: 600,
    minWidth: "auto",
    transition: "color 0.15s ease-in-out",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "transparent",
    },
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: "rgba(8,8,8,0.92)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72, md: 80 },
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#ffffff",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              alt="Ouro"
              src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png"
              sx={{ height: { xs: 28, md: 34 }, display: "block" }}
            />
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: "rgba(255,255,255,0.38)",
                textTransform: "uppercase",
                letterSpacing: "0.14em",
              }}
            >
              beta
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {PUBLIC_NAV_LINKS.map((item) => (
              <Button key={item.to} component={Link} to={item.to} sx={navButtonSx}>
                {item.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isAuthenticated ? (
              <Button
                component={Link}
                to="/lobby"
                variant="contained"
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.25,
                  backgroundColor: "#E8A33D",
                  color: "#0B0D0F",
                  transition: "background-color 0.15s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#B97A1F",
                  },
                }}
              >
                Go to lobby
              </Button>
            ) : (
              <React.Fragment>
                <Button
                  onClick={(event) => setLoginAnchorEl(event.currentTarget)}
                  variant="outlined"
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.25,
                    minHeight: 42,
                    borderColor: "rgba(255,255,255,0.18)",
                    color: "#ffffff",
                    transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.34)",
                      backgroundColor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  Log in
                </Button>
                <Button
                  component="a"
                  href={ctaHref}
                  variant="contained"
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.25,
                    backgroundColor: "#E8A33D",
                    color: "#0B0D0F",
                    transition: "background-color 0.15s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#B97A1F",
                    },
                  }}
                >
                  Join
                </Button>
              </React.Fragment>
            )}

            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                color: "#ffffff",
              }}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {!isAuthenticated ? (
        <Popover
          open={isLoginPopoverOpen}
          anchorEl={loginAnchorEl}
          onClose={() => setLoginAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 300,
              p: 2,
              borderRadius: "6px",
              color: "#ffffff",
              backgroundColor: "#111111",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.34)",
            },
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.75 }}>
            Join with Steam
          </Typography>
          <Typography
            sx={{
              fontSize: "0.88rem",
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.68)",
              mb: 1.5,
            }}
          >
            Steam is the main signup path for Ouro. It is read-only and never
            asks for inventory, trade, or market permissions.
          </Typography>
          <SteamSignInButton
            fullWidth
            showAssurance={false}
            surface="public_header"
            sx={{
              minHeight: 44,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: "#E8A33D",
              color: "#0B0D0F",
              "&:hover": {
                backgroundColor: "#B97A1F",
              },
            }}
          />
          <Typography
            sx={{
              mt: 1.5,
              mb: 1,
              fontSize: "0.78rem",
              color: "rgba(255,255,255,0.48)",
            }}
          >
            Trouble with Steam? Use Google as a fallback.
          </Typography>
          <Box onClick={() => track("sso_google_clicked", { provider: "google" })}>
            <GoogleLogin
              width={260}
              onSuccess={googleResponse}
              onError={onFailure}
            />
          </Box>
        </Popover>
      ) : null}

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: "#0d0d0d",
            color: "#ffffff",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography
            sx={{
              mb: 1.5,
              fontSize: "0.82rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
            }}
          >
            Navigate
          </Typography>
          <List disablePadding>
            {PUBLIC_NAV_LINKS.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  sx={{ borderRadius: "6px" }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding sx={{ mt: 1 }}>
              {isAuthenticated ? (
                <ListItemButton
                  component={Link}
                  to="/lobby"
                  onClick={() => setMobileOpen(false)}
                  sx={{ borderRadius: "6px" }}
                >
                  <ListItemText primary="Go to lobby" />
                </ListItemButton>
              ) : (
                <Box sx={{ width: "100%" }}>
                  <ListItemButton
                    component="a"
                    href={ctaHref}
                    onClick={() => setMobileOpen(false)}
                    sx={{ borderRadius: "6px" }}
                  >
                    <ListItemText primary="Join the founding community" />
                  </ListItemButton>
                  <Box
                    sx={{
                      mt: 1.5,
                      px: 1,
                      py: 1.5,
                      borderRadius: "6px",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, mb: 1 }}>
                      Join with Steam
                    </Typography>
                    <SteamSignInButton
                      fullWidth
                      showAssurance={false}
                      surface="mobile_public_header"
                      sx={{
                        minHeight: 42,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 700,
                        backgroundColor: "#E8A33D",
                        color: "#0B0D0F",
                        "&:hover": {
                          backgroundColor: "#B97A1F",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        mt: 1.5,
                        mb: 1,
                        fontSize: "0.76rem",
                        color: "rgba(255,255,255,0.48)",
                      }}
                    >
                      Google fallback
                    </Typography>
                    <Box onClick={() => track("sso_google_clicked", { provider: "google" })}>
                      <GoogleLogin
                        width={228}
                        onSuccess={googleResponse}
                        onError={onFailure}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}

PublicHeader.propTypes = {
  isAuthenticated: PropTypes.bool,
  googleResponse: PropTypes.func,
  onFailure: PropTypes.func,
};
